// frontend/src/components/SucursalPreciosView.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Vista de "Gestión de Productos" cuando el Dueño elige una sucursal específica
// (no "Todas"). Muestra el catálogo completo con el precio EFECTIVO de esa
// sucursal (especial si existe, general si no) y permite definir/quitar el
// precio especial — pero NO editar el catálogo general ni crear/eliminar
// productos: esas acciones solo existen en la vista "Todas las sucursales",
// a propósito, para que sea imposible confundir "edito el precio de esta
// botica" con "edito el precio de todo el negocio".
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { sucursalesAPI } from '../services/api';
import Toast from './Toast';
import './SucursalPreciosView.css';

export const PrecioEspecialModal = ({ producto, sucursalNombre, onClose, onSaved, onError }) => {
    const [valor, setValor] = useState(
        producto.precio_especial !== null && producto.precio_especial !== undefined
            ? String(producto.precio_especial)
            : ''
    );
    const [isSaving, setIsSaving] = useState(false);
    const tieneEspecial = producto.precio_especial !== null && producto.precio_especial !== undefined;

    const handleGuardar = async () => {
        const num = parseFloat(valor);
        if (!valor.trim() || isNaN(num) || num < 0) {
            onError('Ingresa un precio válido');
            return;
        }
        setIsSaving(true);
        try {
            await sucursalesAPI.setPrecioEspecial(producto.sucursal_id_ctx, producto.producto_id, { precio_especial: num });
            onSaved('✅ Precio especial guardado');
        } catch (err) {
            onError(err.response?.data?.message || 'Error al guardar el precio especial');
        } finally {
            setIsSaving(false);
        }
    };

    const handleQuitar = async () => {
        setIsSaving(true);
        try {
            await sucursalesAPI.setPrecioEspecial(producto.sucursal_id_ctx, producto.producto_id, { precio_especial: null });
            onSaved('✅ Precio especial eliminado — vuelve a usar el precio general');
        } catch (err) {
            onError(err.response?.data?.message || 'Error al quitar el precio especial');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="sp-overlay" onClick={onClose}>
            <div className="sp-modal" onClick={e => e.stopPropagation()}>
                <div className="sp-modal-header">
                    <h3>💰 Precio especial</h3>
                    <button className="sp-modal-close" onClick={onClose}>✕</button>
                </div>
                <p className="sp-modal-producto">{producto.name}</p>
                <p className="sp-modal-sucursal">en {sucursalNombre}</p>

                <div className="sp-modal-general">
                    Precio general del catálogo: <strong>S/. {parseFloat(producto.precio_general).toFixed(2)}</strong>
                </div>

                <label className="sp-modal-label">Precio especial para esta sucursal</label>
                <input
                    type="number"
                    step="0.10"
                    min="0"
                    value={valor}
                    onChange={e => setValor(e.target.value)}
                    placeholder="Ej: 12.50"
                    autoFocus
                    className="sp-modal-input"
                />

                <div className="sp-modal-actions">
                    {tieneEspecial && (
                        <button className="sp-btn-quitar" onClick={handleQuitar} disabled={isSaving}>
                            🗑️ Quitar
                        </button>
                    )}
                    <button className="sp-btn-cancelar" onClick={onClose} disabled={isSaving}>Cancelar</button>
                    <button className="sp-btn-guardar" onClick={handleGuardar} disabled={isSaving}>
                        {isSaving ? 'Guardando...' : '✅ Guardar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const SucursalPreciosView = ({ sucursalId, sucursalNombre }) => {
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalTarget, setModalTarget] = useState(null);
    const [toast, setToast] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await sucursalesAPI.getProductos(sucursalId);
            const conCtx = (res.data?.data?.productos || []).map(p => ({ ...p, sucursal_id_ctx: sucursalId }));
            setProductos(conCtx);
        } catch (err) {
            setToast({ message: '❌ Error al cargar productos de la sucursal', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [sucursalId]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (!searchTerm.trim()) return productos;
        const term = searchTerm.toLowerCase();
        return productos.filter(p =>
            p.name.toLowerCase().includes(term) || p.code?.toLowerCase().includes(term)
        );
    }, [productos, searchTerm]);

    const handleSaved = (msg) => {
        setModalTarget(null);
        setToast({ message: msg, type: 'success' });
        load();
    };

    return (
        <div className="sp-view">
            <div className="sp-banner">
                🏪 Viendo precios de <strong>{sucursalNombre}</strong> — el precio de catálogo (general) no se edita aquí.
                Cambia a "🌐 Todas las sucursales" para editar el catálogo completo o crear productos nuevos.
            </div>

            <div className="sp-toolbar">
                <input
                    type="text"
                    placeholder="Buscar producto por nombre o código..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="sp-search"
                />
            </div>

            {loading ? (
                <div className="sp-loading">Cargando...</div>
            ) : filtered.length === 0 ? (
                <div className="sp-empty">No hay productos que coincidan.</div>
            ) : (
                <div className="sp-table-wrapper">
                    <table className="sp-table">
                        <thead>
                            <tr>
                                <th>Código</th>
                                <th>Producto</th>
                                <th>Stock en esta sucursal</th>
                                <th>Precio</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(p => {
                                const tieneEspecial = p.precio_especial !== null && p.precio_especial !== undefined;
                                return (
                                    <tr key={p.producto_id}>
                                        <td className="sp-col-code">{p.code}</td>
                                        <td>{p.name}</td>
                                        <td>
                                            {p.stock_sucursal !== null && p.stock_sucursal !== undefined
                                                ? (p.unit === 'kg' ? parseFloat(p.stock_sucursal).toFixed(2) : Math.floor(p.stock_sucursal))
                                                : <span className="sp-sin-contar">— sin contar aquí —</span>}
                                        </td>
                                        <td>
                                            {tieneEspecial ? (
                                                <div className="sp-precio-cell">
                                                    <span className="sp-precio-especial-valor">S/. {parseFloat(p.precio_especial).toFixed(2)}</span>
                                                    <span className="sp-badge sp-badge-especial">🏪 Especial de {sucursalNombre}</span>
                                                    <span className="sp-precio-tachado">S/. {parseFloat(p.precio_general).toFixed(2)}</span>
                                                </div>
                                            ) : (
                                                <div className="sp-precio-cell">
                                                    <span>S/. {parseFloat(p.precio_general).toFixed(2)}</span>
                                                    <span className="sp-badge sp-badge-general">🌐 Precio general</span>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <button className="sp-btn-editar" onClick={() => setModalTarget(p)}>
                                                {tieneEspecial ? '✏️ Editar' : '💰 Definir precio especial aquí'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {modalTarget && (
                <PrecioEspecialModal
                    producto={modalTarget}
                    sucursalNombre={sucursalNombre}
                    onClose={() => setModalTarget(null)}
                    onSaved={handleSaved}
                    onError={(msg) => setToast({ message: `❌ ${msg}`, type: 'error' })}
                />
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};

export default SucursalPreciosView;
